import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { products } from '../data/products';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/Button';
import { Heart, ArrowLeft, Filter } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const categoryInfo = {
  kings: {
    title: 'Kings',
    description: "Men's streetwear with royal attitude. Command respect in every room.",
    image: 'https://images.unsplash.com/photo-1617137968427-85924c800a22?auto=format&fit=crop&q=80&w=1920'
  },
  queens: {
    title: 'Queens',
    description: "Women's fashion that commands respect. Reign supreme in style.",
    image: 'https://images.unsplash.com/photo-1609505848912-b7c3b8b4beda?auto=format&fit=crop&q=80&w=1920'
  },
  heir: {
    title: 'Heir',
    description: "Boys' apparel for young kings. Future leaders dress the part.",
    image: 'https://images.unsplash.com/photo-1519457431-44ccd64a579b?auto=format&fit=crop&q=80&w=1920'
  },
  heiress: {
    title: 'Heiress',
    description: "Girls' fashion for young queens. Confidence starts early.",
    image: 'https://images.unsplash.com/photo-1518831959646-742c3a14ebf7?auto=format&fit=crop&q=80&w=1920'
  },
  electronics: {
    title: 'Electronics',
    description: 'Next-gen entertainment & gaming gear for the digital native.',
    image: 'https://images.unsplash.com/photo-1498049794561-7780e7231661?auto=format&fit=crop&q=80&w=1920'
  },
  home: {
    title: 'Home',
    description: 'Urban living essentials to elevate your personal sanctuary.',
    image: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?auto=format&fit=crop&q=80&w=1920'
  },
  sound: {
    title: 'Sound',
    description: 'Premium audio experiences. Hear the world differently.',
    image: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&q=80&w=1920'
  },
  kids: {
    title: 'KIDS!',
    description: 'For the little legends. Style starts early.',
    image: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&q=80&w=1920'
  },
  deals: {
    title: 'Deals',
    description: "Exclusive limited time offers. Don't sleep on these.",
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=1920'
  }
};
export function CollectionPage() {
  const {
    categoryId
  } = useParams<{
    categoryId: string;
  }>();
  const {
    addItem
  } = useCart();
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryId]);
  const category = categoryId as keyof typeof categoryInfo;
  const info = categoryInfo[category];
  const categoryProducts = products.filter(p => p.category === category);
  if (!info) {
    return <div className="min-h-screen bg-uncommon-black text-white flex items-center justify-center" data-id="element-238">
        <div className="text-center" data-id="element-239">
          <h1 className="text-4xl font-bold mb-4" data-id="element-240">Category Not Found</h1>
          <Link to="/" className="text-uncommon-gold hover:underline" data-id="element-241">
            Return Home
          </Link>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-uncommon-black flex flex-col" data-id="element-242">
      <Header data-id="element-243" />

      {/* Hero Section */}
      <div className="relative h-[40vh] min-h-[400px] w-full overflow-hidden" data-id="element-244">
        <div className="absolute inset-0" data-id="element-245">
          <img src={info.image} alt={info.title} className="w-full h-full object-cover opacity-50" data-id="element-246" />
          <div className="absolute inset-0 bg-gradient-to-t from-uncommon-black via-uncommon-black/50 to-transparent" data-id="element-247" />
        </div>

        <div className="absolute inset-0 flex flex-col justify-end pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-248">
          <Link to="/" className="inline-flex items-center text-gray-400 hover:text-white mb-6 transition-colors" data-id="element-249">
            <ArrowLeft size={16} className="mr-2" data-id="element-250" /> Back to Home
          </Link>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-251">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-4" data-id="element-252">
              <span className="text-uncommon-gold" data-id="element-253">U</span>NCMMN {info.title}
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl" data-id="element-254">
              {info.description}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Filter / Sort Bar */}
      <div className="border-y border-uncommon-grey bg-uncommon-dark sticky top-[73px] z-30" data-id="element-255">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center" data-id="element-256">
          <span className="text-gray-400 text-sm font-medium" data-id="element-257">
            Showing {categoryProducts.length} Products
          </span>
          <button className="flex items-center text-white hover:text-uncommon-gold transition-colors text-sm font-bold uppercase tracking-wider" data-id="element-258">
            <Filter size={16} className="mr-2" data-id="element-259" /> Filter & Sort
          </button>
        </div>
      </div>

      {/* Product Grid */}
      <main className="flex-grow py-16 px-4 sm:px-6 lg:px-8" data-id="element-260">
        <div className="max-w-7xl mx-auto" data-id="element-261">
          {categoryProducts.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-262">
              {categoryProducts.map((product, index) => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05
          }} className="group" data-id="element-263">
                  {/* Image Container */}
                  <div className="relative aspect-[3/4] overflow-hidden rounded-lg bg-uncommon-dark mb-4 border border-uncommon-grey group-hover:border-uncommon-gold/50 transition-colors" data-id="element-264">
                    {product.badge && <div className="absolute top-3 left-3 z-10 bg-uncommon-gold text-black text-xs font-bold px-2 py-1 uppercase tracking-wider" data-id="element-265">
                        {product.badge}
                      </div>}
                    <button className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-uncommon-gold hover:text-black transition-colors opacity-0 group-hover:opacity-100" data-id="element-266">
                      <Heart size={16} data-id="element-267" />
                    </button>
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-268" />
                    <div className="absolute inset-x-0 bottom-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300" data-id="element-269">
                      <Button variant="primary" fullWidth className="text-xs py-2" onClick={() => addItem(product)} data-id="element-270">
                        Add to Cart
                      </Button>
                    </div>
                  </div>

                  {/* Info */}
                  <div data-id="element-271">
                    <h3 className="text-lg font-bold text-white mb-1 group-hover:text-uncommon-gold transition-colors truncate" data-id="element-272">
                      {product.name}
                    </h3>
                    <div className="flex justify-between items-center" data-id="element-273">
                      <p className="text-gray-300 font-medium" data-id="element-274">
                        ${product.price.toFixed(2)}
                      </p>
                      <span className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-275">
                        {product.category}
                      </span>
                    </div>
                  </div>
                </motion.div>)}
            </div> : <div className="text-center py-24" data-id="element-276">
              <p className="text-gray-400 text-xl" data-id="element-277">
                No products found in this collection yet.
              </p>
              <Button variant="secondary" className="mt-6" onClick={() => window.history.back()} data-id="element-278">
                Go Back
              </Button>
            </div>}
        </div>
      </main>

      <Footer data-id="element-279" />
    </div>;
}